<template>
  <div class="minimized-bar">
    <div
      v-if="minimizedGoals.length > 0"
      class="stack text-medium-1"
      :class="{ expanded: stackState[ENTITY_TYPE.GOAL] === 'expanded' }"
      @click="onStackClick(ENTITY_TYPE.GOAL)"
      v-click-outside="() => collapseStack(ENTITY_TYPE.GOAL)"
    >
      <div
        v-for="(item, index) in minimizedGoals"
        class="single-block"
        :style="getBlockStyle(ENTITY_TYPE.GOAL, index)"
        :key="item._id"
        @click="onBlockClick($event, ENTITY_TYPE.GOAL, item)"
        tabindex="-1"
        @keydown.esc="collapseStack(ENTITY_TYPE.GOAL)"
        v-no-capture
      >
        <render-slot :view="item.view" />
        <span class="peg" @click.stop="collapseStack(ENTITY_TYPE.GOAL)"></span>
      </div>
    </div>
    <div
      v-if="minimizedKRs.length > 0"
      class="stack text-medium-1"
      :class="{ expanded: stackState[ENTITY_TYPE.KEY_RESULT] === 'expanded' }"
      @click="onStackClick(ENTITY_TYPE.KEY_RESULT)"
      v-click-outside="() => collapseStack(ENTITY_TYPE.KEY_RESULT)"
    >
      <div
        v-for="(item, index) in minimizedKRs"
        class="single-block"
        :style="getBlockStyle(ENTITY_TYPE.KEY_RESULT, index)"
        :key="item._id"
        @click="onBlockClick($event, ENTITY_TYPE.KEY_RESULT, item)"
        tabindex="-1"
        @keydown.esc="collapseStack(ENTITY_TYPE.KEY_RESULT)"
        v-no-capture
      >
        <render-slot :view="item.view" />
        <span class="peg" @click.stop="collapseStack(ENTITY_TYPE.KEY_RESULT)"></span>
      </div>
    </div>
    <div
      v-if="minimizedInitiatives.length > 0"
      class="stack text-medium-1"
      :class="{ expanded: stackState[ENTITY_TYPE.INITIATIVE] === 'expanded' }"
      @click="onStackClick(ENTITY_TYPE.INITIATIVE)"
      v-click-outside="() => collapseStack(ENTITY_TYPE.INITIATIVE)"
    >
      <div
        v-for="(item, index) in minimizedInitiatives"
        class="single-block"
        :style="getBlockStyle(ENTITY_TYPE.INITIATIVE, index)"
        :key="item._id"
        @click="onBlockClick($event, ENTITY_TYPE.INITIATIVE, item)"
        tabindex="-1"
        @keydown.esc="collapseStack(ENTITY_TYPE.INITIATIVE)"
        v-no-capture
      >
        <render-slot :view="item.view" />
        <span class="peg" @click.stop="collapseStack(ENTITY_TYPE.INITIATIVE)"></span>
      </div>
    </div>
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import RenderSlot from '@/components/RenderSlot'

export default {
  name: 'MinimizedItemsBar',
  mixins: [EventBusMixin],
  props: ['orgId', 'minimizedItems'],
  components: {
    RenderSlot
  },
  data() {
    return {
      ENTITY_TYPE,
      stackState: {
        [ENTITY_TYPE.INITIATIVE]: 'collapsed',
        [ENTITY_TYPE.KEY_RESULT]: 'collapsed',
        [ENTITY_TYPE.GOAL]: 'collapsed'
      }
    }
  },
  computed: {
    minimizedGoals() {
      return this.minimizedItems.filter(x => x.type === ENTITY_TYPE.GOAL)
    },
    minimizedKRs() {
      return this.minimizedItems.filter(x => x.type === ENTITY_TYPE.KEY_RESULT)
    },
    minimizedInitiatives() {
      return this.minimizedItems.filter(x => x.type === ENTITY_TYPE.INITIATIVE)
    },
    entityItemsMap() {
      return {
        [ENTITY_TYPE.INITIATIVE]: this.minimizedInitiatives,
        [ENTITY_TYPE.GOAL]: this.minimizedGoals,
        [ENTITY_TYPE.KEY_RESULT]: this.minimizedKRs
      }
    }
  },
  methods: {
    trackEvent(action, type) {
      let eventName
      if (type === ENTITY_TYPE.KEY_RESULT) {
        eventName = EVENT_NAME.KR
      } else if (type === ENTITY_TYPE.INITIATIVE) {
        eventName = EVENT_NAME.INITIATIVE
      } else if (type === ENTITY_TYPE.GOAL) {
        eventName = EVENT_NAME.GOAL
      }
      if (eventName) {
        trackEvent(EVENT_CATEGORY.NAVIGATION, action, eventName)
      }
    },
    maximize(payload) {
      this.eventBusEmit(events.MODAL_MAXIMIZE, payload)
      this.trackEvent(EVENT_ACTION.DOCK_CLICK, payload.type)
    },
    onStackClick(entity) {
      if (this.stackState[entity] === 'collapsed') {
        this.stackState[entity] = 'expanded'
        this.stackState = {
          ...this.stackState,
          [entity]: 'expanded'
        }
        this.trackEvent(EVENT_ACTION.DOCK_FAN, entity)
      } else {
        this.stackState[entity] = 'collapsed'
      }
    },
    getBlockStyle(entity, index) {
      const items = this.entityItemsMap[entity]
      const reverseIndex = items.length - 1 - index
      const baseScaleChange = 0.033333333
      const baseTranslateChange = 0.5
      const collapsedTransform = `scale(${1 -
        reverseIndex * baseScaleChange}) translateY(-${baseTranslateChange * reverseIndex}rem)`
      const expandedTransform = `rotate(${reverseIndex * 1.1}deg) translate(-${reverseIndex *
        0.2}rem, -${reverseIndex * 3}rem)`
      return {
        transform: this.stackState[entity] === 'expanded' ? expandedTransform : collapsedTransform
      }
    },
    collapseStack(entity) {
      this.stackState = {
        ...this.stackState,
        [entity]: 'collapsed'
      }
    },
    onBlockClick($event, entity, payload) {
      const items = this.entityItemsMap[entity]
      if (this.stackState[entity] === 'expanded' || items.length === 1) {
        this.maximize(payload)
        $event.stopPropagation()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.single-block {
  position: absolute;
  bottom: 0;
  background: $documentColor;
  box-shadow: $menuShadow;
  border-radius: 10px;
  transition: ease-in-out 0.2s;
  width: 300px;
  padding: 10px 15px;
  text-align: left;
}

.stack {
  width: 300px;

  &:not(:last-child) {
    margin-right: 15px;
  }
}

.minimized-bar {
  position: fixed;
  max-height: 80vh;
  bottom: 20px;
  text-align: center;
  cursor: pointer;
  transition: transform ease-in-out 0.1s;
  z-index: 2;
}

.minimized-bar {
  display: flex;
}

.expanded {
  transform: translateY(-35px);
}

.peg {
  cursor: default;
  background: transparent;
  height: 8px;
  width: 300px;
  display: block;
  bottom: -8px;
  position: absolute;
  left: 0;
}
</style>
